import { cn } from '@tempo-io/sui-class-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizableHandle } from '@/lib/hooks/common/useResizableHandle';
import {
  PARENT_HEADER_HEIGHT,
  RESIZE_HANDLE_CENTER_OFFSET,
  RESIZE_HANDLE_HITBOX_PADDING,
  RESIZE_HANDLE_WIDTH,
  VERTICAL_ROW_HEADER_WIDTH,
} from '../useTimelineColumnDayWidth';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from './TimelineRowAxisLabel';

const MIN_WIDTH = 120;
const MAX_WIDTH = 600;

interface TimelineRowHeaderResizeProps {
  rowHeaderWidth: number;
  onRowHeaderWidthChange: (width: number) => void;
  hasVerticalSubHeaders: boolean;
  isVertical: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  canEdit: boolean;
  isPublishedMode: boolean;
}

export function TimelineRowHeaderResize({
  rowHeaderWidth,
  onRowHeaderWidthChange,
  hasVerticalSubHeaders,
  isVertical,
  scrollContainerRef,
  canEdit,
  isPublishedMode,
}: TimelineRowHeaderResizeProps) {
  const headerWidth =
    isVertical && !hasVerticalSubHeaders ? VERTICAL_ROW_HEADER_WIDTH : rowHeaderWidth;
  const headerOffset =
    TIMELINE_ROW_AXIS_LABEL_WIDTH + (hasVerticalSubHeaders ? PARENT_HEADER_HEIGHT : 0);
  const [containerLeft, setContainerLeft] = useState(0);
  const [containerTop, setContainerTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const isDraggingRef = useRef(false);

  const updateContainerPosition = useCallback(() => {
    if (isDraggingRef.current) return;
    const element = scrollContainerRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setContainerLeft(rect.left || 0);
    setContainerTop(rect.top || 0);
    setContainerHeight(rect.height || 0);
  }, [scrollContainerRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollContainerRef.current in deps is a React anti-pattern; ref mutations don't trigger re-renders
  useEffect(() => {
    updateContainerPosition();
    const element = scrollContainerRef.current;
    if (!element || !canEdit || isPublishedMode) return;

    const resizeObserver = new ResizeObserver(() => updateContainerPosition());
    resizeObserver.observe(element);
    window.addEventListener('resize', updateContainerPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContainerPosition);
    };
  }, [updateContainerPosition, canEdit, isPublishedMode]);

  const { currentWidth, isDragging, handleRef, containerProps } = useResizableHandle({
    initialWidth: headerWidth,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    onWidthChange: onRowHeaderWidthChange,
    enabled: true,
  });

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  if (!canEdit || isPublishedMode || (isVertical && !hasVerticalSubHeaders)) return null;

  const handlePosition = currentWidth + containerLeft + headerOffset;

  return (
    <div
      data-testid="timeline-resize-container"
      {...containerProps}
      style={{
        cursor: 'col-resize',
        position: 'fixed',
        top: `${containerTop}px`,
        height: `${containerHeight}px`,
        zIndex: 30,
        pointerEvents: 'auto',
        ...(isDragging
          ? { left: 0, right: 0 }
          : {
              left: `${handlePosition - RESIZE_HANDLE_HITBOX_PADDING}px`,
              width: `${RESIZE_HANDLE_HITBOX_PADDING * 2}px`,
            }),
      }}
    >
      <div
        ref={handleRef}
        role="slider"
        tabIndex={0}
        aria-valuenow={currentWidth}
        aria-label="Adjust the Row Label Width"
        data-dragging={isDragging}
        className={cn(
          'fixed z-100 bg-bg-neutral transition-opacity duration-120 pointer-events-auto',
          'opacity-0 hover:opacity-100 data-[dragging=true]:opacity-100',
          'hover:bg-bg-neutral-bold data-[dragging=true]:bg-bg-neutral-bold/80',
        )}
        style={{
          left: `${handlePosition - RESIZE_HANDLE_CENTER_OFFSET}px`,
          top: `${containerTop}px`,
          height: `${containerHeight}px`,
          width: `${RESIZE_HANDLE_WIDTH}px`,
        }}
      />
    </div>
  );
}
